import {
  DestroyRef,
  ChangeDetectorRef,
  inject,
  Injectable,
  ViewContainerRef,
  ViewRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { NotificationService } from '@progress/kendo-angular-notification';
import { Download } from '@comp/documents/documents.model';

@Injectable({
  providedIn: 'root', // Ensures the service is a singleton
})
export class CommonService {
  notificationService: NotificationService = inject(NotificationService);

  formatDateYearDayMonth(d: Date): string {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(d.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  getTimeHoursMinutes(d: Date): string {
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  getMeridian(d: Date): string {
    return d.getHours() < 12 ? 'AM' : 'PM';
  }

  getTimeZoneAbbreviation(d: Date): string {
    return d
      .toLocaleTimeString('en-us', { timeZoneName: 'short' })
      .split(' ')[2];
  }

  formatSerialNumber(serialNumber: string): string {
    if (serialNumber.length == 6) {
      return [serialNumber.slice(0, 3), '-', serialNumber.slice(3)].join('');
    } else {
      return serialNumber;
    }
  }

  // Refactored Version...
  onDestroy$(): Subject<void> {
    const destroy$ = new Subject<void>();
    const destroyRef = inject(DestroyRef);
    destroyRef.onDestroy(() => {
      destroy$.next();
      destroy$.complete();
    });
    return destroy$;
  }

  appendSuccessNotification(
    message: string,
    horizontalPos: 'left' | 'center' | 'right',
    verticalPos: 'top' | 'bottom',
    appendTo: ViewContainerRef,
  ) {
    this.notificationService.show({
      appendTo: appendTo,
      content: message,
      animation: { type: 'fade', duration: 500 },
      type: { style: 'success', icon: true },
      position: {
        horizontal: horizontalPos,
        vertical: verticalPos,
      },
    });
  }

  successNotification(
    message: string,
    horizontalPos: 'left' | 'center' | 'right',
    verticalPos: 'top' | 'bottom',
  ) {
    this.notificationService.show({
      content: message,
      animation: { type: 'fade', duration: 500 },
      type: { style: 'success', icon: true },
      position: {
        horizontal: horizontalPos,
        vertical: verticalPos,
      },
    });
  }

  appendErrorNotification(
    message: string,
    horizontalPos: 'left' | 'center' | 'right',
    verticalPos: 'top' | 'bottom',
    appendTo: ViewContainerRef,
    duration = 500,
  ) {
    this.notificationService.show({
      appendTo: appendTo,
      content: message,
      animation: { type: 'fade', duration: duration },
      type: { style: 'error', icon: true },
      position: {
        horizontal: horizontalPos,
        vertical: verticalPos,
      },
    });
  }

  errorNotification(
    message: string,
    horizontalPos: 'left' | 'center' | 'right',
    verticalPos: 'top' | 'bottom',
    duration = 500,
  ) {
    this.notificationService.show({
      content: message,
      animation: { type: 'fade', duration: duration },
      type: { style: 'error', icon: true },
      position: {
        horizontal: horizontalPos,
        vertical: verticalPos,
      },
    });
  }

  // This gets a parameter from the current route.  We could pass in the param name.
  // const getPageParam = () => {
  //   return inject(ActivatedRoute).queryParams.pipe(
  //     filter(params => params['page']),
  //     map(params => params['page'])
  //   );
  // };

  getDownloads(): Download[] {
    return [
      {
        type: 'VPN Client',
        name: 'SoftPro VPN Client 64 Bit',
        file: 'GlobalProtect64.msi',
        description:
          'Required installation to provide you with a secure connection to your SoftPro Hosted environment.',
      },
      {
        type: 'Select',
        name: 'SoftPro Select Client v4.3.75.4.3',
        file: 'Select-4.3.75.4.3.zip',
        description:
          'SoftPro Select client installation to access your software.',
      },
      {
        type: 'Select',
        name: 'SoftPro Select Client v4.3.73.3.1',
        file: 'Select-4.3.73.3.1.zip',
        description:
          'SoftPro Select client installation to access your software.',
      },
      {
        type: 'Instructions',
        name: 'Select and VPN Install Instructions',
        file: 'Select and Global VPN Install.pdf',
        description:
          'Step-by-step instructions on how to download and install the VPN and SoftPro Select Client.',
      },
      {
        type: 'Tool',
        name: 'Toggle Settlement Type',
        file: 'SoftPro.Select.Toggler_v1.4.0.0.zip',
        description:
          'Provides the ability for a user to easily change the settlement type within a file between a CD, HUD or Commercial Settlement Statement (CSS).',
      },
      {
        type: 'Standard',
        name: 'Softpro Standard version 20HF5',
        file: 'STDv20HF5.exe',
        description: 'Document Package updated 6/3/2026',
      },
      {
        type: 'Standard',
        name: 'Softpro Standard version 20HF5 (Release Notes)',
        file: 'Standard_Document_Releases.pdf',
        description: 'Document Package Release Notes updated 6/3/2026',
      },
    ];
  }

  getFaqDownloads(): Download[] {
    return [
      {
        type: 'General',
        name: 'SoftPro, LLC W-9',
        file: 'SoftProLLC_W-9.pdf',
        description: "SoftPro, LLC's W-9 Form",
      },
      {
        type: 'General',
        name: 'USA Digital Solutions, Inc. W-9',
        file: 'USADigitalSolutionsInc_W-9.pdf',
        description: "USA Digital Solutions, Inc's W-9 Form",
      },
    ];
  }
}
